/* Register the ChainFlow OS service worker in production-style environments.
   We intentionally skip registration during local dev (`localhost`) to avoid
   stale caches getting in the way of hot reload.

   Cache-bust discipline
   ---------------------
   When `public/service-worker.js` bumps its VERSION constant, the new SW
   transitions through:
     1. `install`      → preload SHELL + skipWaiting
     2. `activate`     → wipe every prior-version cache + clients.claim
     3. `controllerchange` (in the page) → we force a one-time reload so
        the page evaluates the freshly-cached JS bundle instead of the
        stale one currently in memory.

   The auto-reload guard (`_swReloaded`) prevents the controllerchange
   handler from looping when a SW update is mid-activation. */

let _swReloaded = false;

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Skip on plain localhost / 127.0.0.1 dev so HMR isn't fighting the cache.
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // When a NEW worker takes over (after VERSION bump in
        // service-worker.js), force the page to reload exactly once
        // so the user picks up the fresh JS bundle instead of
        // continuing to execute the in-memory stale one.
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (_swReloaded) return;
          _swReloaded = true;
          window.location.reload();
        });
        // If a new SW is already waiting at this point, ask it to
        // activate immediately. Combined with the controllerchange
        // handler above this guarantees the latest bundle on next
        // tab load.
        if (reg && reg.waiting) {
          try { reg.waiting.postMessage({ type: "SKIP_WAITING" }); } catch { /* noop */ }
        }
      })
      .catch((err) => {
        // Non-fatal — app must work without SW
        // eslint-disable-next-line no-console
        console.warn("[ChainFlow] Service worker registration failed:", err);
      });

    // Service worker can ask the page to navigate (notificationclick handler).
    navigator.serviceWorker.addEventListener("message", (evt) => {
      const data = evt && evt.data;
      if (data && data.type === "cf:navigate" && typeof data.url === "string") {
        try { window.location.href = data.url; } catch { /* noop */ }
      }
    });
  });
}

export default registerServiceWorker;
